import {
  DecoratorBlockNode,
  type SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import { addClassNamesToElement } from "@lexical/utils";
import {
  type DOMConversionMap,
  type DOMExportOutput,
  type ElementFormatType,
  type LexicalNode,
  type NodeKey,
  type Spread,
} from "lexical";
import type { ButtonNodeBlock } from "../types/markdown-node-blocks";
import { headlessDecorate } from "./headless-decorate";
import { convertButtonListElement, isButtonListElement } from "./utils";
export type SerializedButtonListNode = Spread<
  {
    buttons: ButtonNodeBlock[];
    type: "buttonlist";
  },
  SerializedDecoratorBlockNode
>;

export class ButtonListNode extends DecoratorBlockNode {
  __buttons: ButtonNodeBlock[];

  constructor(buttons: ButtonNodeBlock[], format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__buttons = buttons;
  }

  decorate(): React.JSX.Element {
    return headlessDecorate();
  }

  static getType(): string {
    return "buttonlist";
  }

  static clone(node: ButtonListNode): ButtonListNode {
    return new this(node.__buttons, node.__format, node.__key);
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("span");
    addClassNamesToElement(dom, "decorator-block-node");
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const dom = document.createElement("div");
    dom.setAttribute("data-node-type", "button-list-node");
    this.__buttons.forEach((button) => {
      const span = document.createElement("span");
      const buttonEl = document.createElement("a");
      buttonEl.style.appearance = "button";
      buttonEl.style.textDecoration = "none";
      buttonEl.href = button.url;
      buttonEl.textContent = button.text;
      // @ts-expect-error we don't know if this is client or server code either
      span.append(buttonEl);
      // @ts-expect-error we don't know if this is client or server code either
      dom.append(span);
    });
    return { element: dom };
  }

  static importJSON(serializedNode: SerializedButtonListNode): ButtonListNode {
    return new this(serializedNode.buttons, serializedNode.format);
  }

  exportJSON(): SerializedButtonListNode {
    return {
      type: "buttonlist",
      buttons: this.__buttons,
      format: this.__format,
      version: 1,
    };
  }

  static makeChatBlock(buttons: ButtonNodeBlock[]): {
    type: "buttonlist";
    buttons: ButtonNodeBlock[];
  } {
    return { type: "buttonlist", buttons };
  }

  getChatBlock(): { type: "buttonlist"; buttons: ButtonNodeBlock[] } {
    return ButtonListNode.makeChatBlock(this.__buttons);
  }

  setButtons(buttons: ButtonNodeBlock[]): void {
    const writable = this.getWritable();
    writable.__buttons = buttons;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (imageNode: HTMLElement) => {
        if (!isButtonListElement(imageNode)) return null;

        return {
          conversion: (n) => convertButtonListElement(n, ButtonListNode),
          priority: 0,
        };
      },
    };
  }
}

export function $isButtonListNode(node: LexicalNode | null | undefined): node is ButtonListNode {
  return node instanceof ButtonListNode;
}
